import * as React from 'react'

import { Box, type Icon, Text, Title } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import groupBy from 'lodash/groupBy'

import BoxList from 'modules/core/components/BoxList'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import type Leave from 'modules/timeoff/store/models/Leave'

import { Medical, Other } from './Item'
import MedicalLeaveTooltip from './MedicalLeaveTooltip'
import PastLeaves from './PastLeaves'
import styles from './index.module.scss'

const cx = classNames.bind(styles)

const illustrations: Record<string, React.ComponentProps<typeof Icon.Large>['icon']> = {
  past: 'calendar',
  pending_review: 'holidays-event-go',
  rejected: 'holidays-event-cancel',
  medical: 'holidays-event-date',
  current: 'holidays-event-date',
  upcoming: 'holidays-event-date',
}

export type LeaveSectionType = 'medical' | 'upcoming' | 'past' | 'rejected' | 'pending_review'

interface Props {
  employee: Employee
  leaves: Leave[]
  type: LeaveSectionType
  year: number
}

function group(props: Props) {
  const { leaves, type } = props

  return groupBy(leaves, (leave: Leave) => {
    const isMedical = leave.leaveType?.isMedicalLeave
    const isPast = type === 'past'

    if (isMedical) return 'medical'

    if (isPast) return 'past'

    return 'other'
  })
}

function description(props: Props) {
  const { employee, type } = props

  const params = {
    name: employeeUser(employee).preferredName,
  }

  if (employee.policy.can('approveLeaves')) {
    const current = employeeUser(employee).isCurrent()

    switch (type) {
      case 'medical':
        return i18n.tCurrent('time_off.medical.subtitle.admin', current, params)
      case 'upcoming':
        return i18n.tCurrent('time_off.upcoming.subtitle.admin', current, params)
      case 'past':
        return i18n.tCurrent('time_off.past.subtitle.admin', current, params)
      case 'rejected':
        return i18n.tCurrent('time_off.rejected.subtitle.admin', current, params)
      case 'pending_review':
        return i18n.tCurrent('time_off.pending_review.subtitle.admin', current, params)
      default:
        throw new Error(`Unknown leave section type: ${type}`)
    }
  } else {
    switch (type) {
      case 'medical':
        return i18n.t('time_off.medical.subtitle.basic.description', params)
      case 'upcoming':
        return i18n.t('time_off.upcoming.subtitle.basic.description', params)
      case 'past':
        return i18n.t('time_off.past.subtitle.basic.description', params)
      case 'rejected':
        return i18n.t('time_off.rejected.subtitle.basic.description', params)
      case 'pending_review':
        return i18n.t('time_off.pending_review.subtitle.basic.description', params)
      default:
        throw new Error(`Unknown leave section type: ${type}`)
    }
  }
}

function title(props: Props) {
  const { type } = props

  switch (type) {
    case 'medical':
      return i18n.t('time_off.medical.title')

    case 'upcoming':
      return i18n.t('time_off.upcoming.title')

    case 'past':
      return i18n.t('time_off.past.title')

    case 'rejected':
      return i18n.t('time_off.rejected.title')

    case 'pending_review':
      return i18n.t('time_off.pending_review.title')

    default:
      throw new Error(`Unknown leave section type: ${type}`)
  }
}

function canUpdate(props: Props) {
  const { employee } = props

  return employee.policy.can('updateLeaves')
}

function canManageLeaveDocuments(props: Props) {
  const { employee } = props

  return employee.policy.can('manageLeavesDocuments')
}

function canEdit(props: Props) {
  return canUpdate(props) || canManageLeaveDocuments(props)
}

function renderItem(leave: Leave) {
  const medical = leave.leaveType?.isMedicalLeave
  const isLegacyMedical = !!leave.attributes.medical_leave_id
  const Component = isLegacyMedical ? Medical : Other

  return (
    <div
      key={leave.attributes.id}
      className={cx('itemContainer', {
        medical,
      })}
    >
      <Component leave={leave} />
    </div>
  )
}

export default function Section(props: Props) {
  const { type, year, employee, leaves } = props
  const isPast = type === 'past'
  const hasLeaves = leaves.length !== 0
  const groups = group(props)
  const illustration = illustrations[type]

  return (
    <Box>
      <Title
        variant="500"
        illustration={illustration}
        title={title(props)}
        description={description(props)}
      />
      {groups.medical &&
        groups.medical.map((leave) => (
          <React.Fragment key={leave.attributes.id}>
            {canEdit(props) && (
              <MedicalLeaveTooltip key={`${leave.attributes.id}-tooltip`} leave={leave} />
            )}
            {renderItem(leave)}
          </React.Fragment>
        ))}
      {isPast && (
        <>
          {!!groups.past && <BoxList>{groups.past.map(renderItem)}</BoxList>}
          {!hasLeaves && (
            <Text.Normal>{i18n.t('time_off.past.no_past_absences_current_year')}</Text.Normal>
          )}
          <PastLeaves employee={employee} year={year} />
        </>
      )}
      {!!groups.other && <BoxList>{groups.other.map(renderItem)}</BoxList>}
    </Box>
  )
}
